/**
 * The replication status line, shown only when replication has something to say.
 *
 * A line that reads "up to date" every second of every day is furniture: it is right so often
 * that nobody reads it, and it takes up the place where a real warning would appear. So the
 * healthy states — synced, and the background poll — render nothing at all, and the line
 * appears exactly when work is queued, the device is offline, or the server refused something.
 *
 * There is no animated indicator either: the status only changes when something actually
 * changed, and a blinking badge would draw attention away from the roll call.
 */

import type { ReactNode } from 'react';

import { SyncStatus } from '../data/store.js';
import { useReplicaVersion, useStore } from '../data/context.js';
import { formatClock, plural } from './format.js';
import { AlertIcon, CheckIcon, OfflineIcon, SyncIcon } from './icons.js';

/** What the status line shows for one sync state. */
interface StatusPresentation {
  icon: ReactNode;
  message: string;
  className: string;
}

/** Builds the wording for the current sync state. */
function present(
  status: SyncStatus,
  pending: number,
  lastSyncedAt: number | null,
  lastError: string | null,
): StatusPresentation {
  const queued = `${pending} ${plural(pending, 'change', 'changes')} waiting to upload`;
  switch (status) {
    case SyncStatus.Synced:
      return lastError !== null
        ? { icon: <AlertIcon size={16} />, message: lastError, className: 'text-missing-text' }
        : {
          icon: <CheckIcon size={16} />,
          message: lastSyncedAt === null ? 'Up to date' : `Up to date · ${formatClock(lastSyncedAt)}`,
          className: 'text-ink-2',
        };
    case SyncStatus.Syncing:
      return { icon: <SyncIcon size={16} />, message: pending > 0 ? `Uploading · ${queued}` : 'Checking for updates', className: 'text-ink-2' };
    case SyncStatus.Offline:
      return {
        icon: <OfflineIcon size={16} />,
        message: pending > 0 ? `Offline · ${queued}` : 'Offline · everything is saved on this device',
        className: 'text-accent-ink',
      };
    case SyncStatus.Failed:
      return { icon: <AlertIcon size={16} />, message: lastError ?? 'Sync failed', className: 'text-missing-text' };
    case SyncStatus.SignedOut:
      return {
        icon: <OfflineIcon size={16} />,
        message: pending > 0 ? `Not connected · ${queued}` : 'Not connected',
        className: 'text-ink-3',
      };
  }
}

/** Returns true when replication is in a state worth reporting. */
function worthReporting(status: SyncStatus, pending: number, lastError: string | null): boolean {
  return pending > 0
    || lastError !== null
    || status === SyncStatus.Offline
    || status === SyncStatus.Failed;
}

/**
 * Renders the current replication status as a single line of text with a leading icon, or
 * nothing while there is nothing to report.
 */
export function ConnectionRow({ className = '' }: { className?: string }): ReactNode {
  const store = useStore();
  useReplicaVersion();
  const { status, pending, lastSyncedAt, lastError } = store.syncState;
  if (!worthReporting(status, pending, lastError)) return null;
  const { icon, message, className: tone } = present(status, pending, lastSyncedAt, lastError);

  return (
    <p className={`flex items-center gap-2 text-tiny ${tone} ${className}`} role="status">
      <span className="shrink-0">{icon}</span>
      <span className="truncate">{message}</span>
    </p>
  );
}
